import { FC, useEffect, useRef, useState } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

import {
  Box,
  Center,
  HStack,
  Progress,
  ProgressFilledTrack,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

export interface CountryProgressItem {
  completed: number;
  country: string;
  failed?: number;
  label: string;
  total: number;
}

interface CountriesProgressProps {
  countries: CountryProgressItem[];
}

const toFlag = (code: string) =>
  [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");

const CountriesSkeleton: FC = () => (
  <VStack className="gap-4">
    <Center className="mb-8">
      <Skeleton className="h-12 w-24 rounded" />
    </Center>
    <Box className="flex-row flex-wrap">
      {Array.from({ length: 9 }).map((_, i) => (
        <Box key={i} className="w-1/3 p-1.5">
          <HStack className="flex-1 items-center gap-2 bg-background-50 rounded-lg p-4">
            <Skeleton className="w-5 h-4 rounded" />
            <VStack className="flex-1 gap-1">
              <Skeleton className="h-2.5 w-full rounded" />
              <Skeleton className="h-1.5 w-full rounded-full" />
            </VStack>
            <Skeleton className="h-2.5 w-6 rounded" />
          </HStack>
        </Box>
      ))}
    </Box>
  </VStack>
);

type CountryCardProps = {
  index: number;
  item: CountryProgressItem;
};

const CountryCard: FC<CountryCardProps> = ({ index, item }) => {
  const { i18n } = useLocalization();
  const { country, label, completed, total } = item;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const delay = index * 80;

  const [progressValue, setProgressValue] = useState(0);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(8);

  useEffect(() => {
    opacity.value = withDelay(delay, withTiming(1, { duration: 300 }));
    translateY.value = withDelay(
      delay,
      withTiming(0, { duration: 300, easing: Easing.out(Easing.ease) })
    );
    const t = setTimeout(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setProgressValue(pct))
      );
    }, delay + 300);
    return () => clearTimeout(t);
  }, [delay, opacity, pct, translateY]);

  const cardStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View style={[cardStyle, { width: "100%" }]}>
      <HStack className="flex-1 items-center gap-2 bg-background-50 rounded-lg p-4">
        <Text className="text-lg">{toFlag(country)}</Text>
        <VStack className="flex-1">
          <Text
            className="text-[11px] font-medium text-typography-700"
            numberOfLines={1}
          >
            {label}
          </Text>
          <Progress
            className="mt-1 bg-background-200"
            size="xs"
            value={progressValue}
          >
            <ProgressFilledTrack className="bg-tertiary-500 [transition:width_0.6s_ease-out]" />
          </Progress>
        </VStack>
        <Text className="text-[10px] font-semibold text-typography-500">
          {i18n.numberToPercentage(pct, { precision: 0 })}
        </Text>
      </HStack>
    </Animated.View>
  );
};

const CountriesProgress: FC<CountriesProgressProps> = ({ countries }) => {
  const totalCompleted = countries.reduce((sum, c) => sum + c.completed, 0);
  const [displayCount, setDisplayCount] = useState(0);
  const prevCountRef = useRef(0);

  useEffect(() => {
    const start = prevCountRef.current;
    const end = totalCompleted;
    prevCountRef.current = end;
    if (start === end) return;
    const steps = 20;
    const delta = (end - start) / steps;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setDisplayCount(Math.round(start + delta * step));
      if (step >= steps) clearInterval(interval);
    }, 40);
    return () => clearInterval(interval);
  }, [totalCompleted]);

  if (countries.length === 0) return <CountriesSkeleton />;

  return (
    <VStack className="gap-4">
      <Center className="mb-8">
        <Text
          className="text-5xl font-light text-typography-900"
          style={{ fontFamily: "Georgia, serif" }}
        >
          {displayCount.toLocaleString()}
        </Text>
      </Center>
      <Box className="flex-row flex-wrap">
        {countries.slice(0, 15).map((item, idx) => (
          <Box key={item.country} className="w-1/3 p-1.5">
            <CountryCard index={idx} item={item} />
          </Box>
        ))}
      </Box>
    </VStack>
  );
};

export default CountriesProgress;
